<template>
  <div class="ocr-report-page-preview">
    <div class="ocr-report-page-preview-head">
      <span>{{ headLabel }}</span>
      <div class="ocr-report-page-preview-actions">
        <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :disabled="!objectUrl" @click="zoomOpen = true">
          Zoom Report
        </v-btn>
        <v-select
          v-model="zoom"
          :items="zoomItems"
          density="compact"
          variant="outlined"
          hide-details
          class="ocr-page-zoom"
          aria-label="Zoom report"
        />
        <v-btn icon size="x-small" variant="text" :disabled="pageIndex <= 0" @click="changePage(-1)">
          <v-icon size="18">mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon size="x-small" variant="text" :disabled="pageIndex >= maxPage" @click="changePage(1)">
          <v-icon size="18">mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>
    <div v-if="loading" class="ocr-report-page-preview-placeholder">Loading page…</div>
    <p v-else-if="error" class="ocr-report-page-preview-error">{{ error }}</p>
    <div v-else-if="objectUrl" class="ocr-report-page-preview-scroll">
      <div class="ocr-page-stage" :style="zoomStyle">
        <img ref="pageImg" :src="objectUrl" class="ocr-report-page-preview-img" alt="" />
        <div
          v-if="manual"
          ref="drawLayer"
          class="ocr-page-draw"
          @pointerdown="onDrawDown"
          @pointermove="onDrawMove"
          @pointerup="onDrawUp"
          @pointercancel="onDrawUp"
        >
          <div
            v-for="(box, index) in boxes"
            :key="index"
            class="ocr-page-box"
            :class="{ 'ocr-page-box--active': index === activeBox }"
            :style="boxStyle(box)"
            @pointerdown.stop="startMove(index, $event)"
          >
            <span class="ocr-page-handle" @pointerdown.stop="startResize(index, $event)" />
          </div>
          <div v-if="draft" class="ocr-page-box ocr-page-box--draft" :style="boxStyle(draft)" />
        </div>
      </div>
    </div>
    <p v-else class="ocr-report-page-preview-placeholder">Select a table to preview its page.</p>
    <div v-if="manual && objectUrl" class="ocr-manual-box-actions">
      <v-btn size="x-small" variant="outlined" class="ocr-toolbar-btn" @click="addBox">Add bounding box</v-btn>
      <v-btn size="x-small" variant="outlined" class="ocr-toolbar-btn" :disabled="activeBox == null" @click="removeBox">
        Remove select
      </v-btn>
      <v-btn size="x-small" variant="outlined" class="ocr-toolbar-btn" @click="resetBoxes">Reset</v-btn>
      <v-btn size="x-small" variant="outlined" class="ocr-toolbar-btn" :disabled="!boxes.length" @click="clearBoxes">
        Clear all
      </v-btn>
    </div>
    <v-dialog v-model="zoomOpen" max-width="960">
      <v-card class="ocr-dialog-card">
        <v-card-title class="ocr-dialog-title">
          <span>Zoom Report</span>
          <v-btn class="ocr-dialog-close" icon="mdi-close" variant="text" aria-label="Close" @click="zoomOpen = false" />
        </v-card-title>
        <v-card-text class="ocr-dialog-body">
          <div class="ocr-field ocr-dialog-zoom">
            <label class="profile-menu-label">Zoom</label>
            <v-select
              v-model="zoom"
              :items="zoomItems"
              density="compact"
              variant="outlined"
              hide-details
              class="ocr-page-zoom"
            />
          </div>
          <div class="ocr-zoom-report-scroll">
            <img v-if="objectUrl" :src="objectUrl" alt="" :style="zoomStyle" />
          </div>
        </v-card-text>
        <v-card-actions class="ocr-dialog-actions">
          <v-spacer />
          <v-btn variant="text" class="ocr-toolbar-btn" @click="zoomOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";

export default {
  name: "OcrReportPagePreview",
  props: {
    filename: { type: String, default: null },
    pageIndex: { type: Number, default: 0 },
    maxPage: { type: Number, default: 0 },
    tableIndex: { type: Number, default: null },
    highlightTables: { type: Boolean, default: true },
    activeTableIndex: { type: Number, default: null },
    manual: { type: Boolean, default: false },
    imageRevision: { type: Number, default: 0 },
  },
  emits: ["update:pageIndex", "update:page-index", "boxes"],
  data() {
    return {
      loading: false,
      error: "",
      objectUrl: "",
      reloadToken: 0,
      reloadQueued: false,
      zoomOpen: false,
      zoom: "Fit",
      zoomItems: ["Fit", "100%", "150%", "200%"],
      boxes: [],
      activeBox: null,
      draft: null,
      drag: null,
    };
  },
  computed: {
    displayPage() {
      return (this.pageIndex ?? 0) + 1;
    },
    headLabel() {
      if (this.tableIndex != null) return `Table image · page ${this.displayPage}`;
      return `Page ${this.displayPage}`;
    },
    zoomStyle() {
      if (this.zoom === "100%") return { width: "100%", maxWidth: "none" };
      if (this.zoom === "150%") return { width: "150%", maxWidth: "none" };
      if (this.zoom === "200%") return { width: "200%", maxWidth: "none" };
      return {};
    },
  },
  watch: {
    filename: {
      immediate: true,
      handler() { this.queueReload(); },
    },
    pageIndex() {
      this.queueReload();
      if (this.manual) this.resetBoxes();
    },
    manual(on) {
      if (on) this.resetBoxes();
      else this.clearBoxes();
    },
    tableIndex() { this.queueReload(); },
    highlightTables() { this.queueReload(); },
    imageRevision() { this.queueReload(); },
    activeTableIndex() { this.queueReload(); },
  },
  beforeUnmount() {
    this.reloadToken += 1;
    this.revoke();
  },
  methods: {
    defaultBox() {
      return { x0: 0.2, y0: 0.2, x1: 0.8, y1: 0.8 };
    },
    emitBoxes() {
      this.$emit(
        "boxes",
        this.boxes.map((box) => ({
          x0: Math.min(box.x0, box.x1),
          y0: Math.min(box.y0, box.y1),
          x1: Math.max(box.x0, box.x1),
          y1: Math.max(box.y0, box.y1),
        }))
      );
    },
    resetBoxes() {
      this.boxes = [this.defaultBox()];
      this.activeBox = 0;
      this.draft = null;
      this.drag = null;
      this.emitBoxes();
    },
    addBox() {
      const shift = Math.min(0.04 * this.boxes.length, 0.16);
      this.boxes.push({ x0: 0.2 + shift, y0: 0.2 + shift, x1: 0.8, y1: 0.8 });
      this.activeBox = this.boxes.length - 1;
      this.emitBoxes();
    },
    removeBox() {
      if (this.activeBox == null) return;
      this.boxes.splice(this.activeBox, 1);
      this.activeBox = this.boxes.length ? Math.min(this.activeBox, this.boxes.length - 1) : null;
      this.emitBoxes();
    },
    clearBoxes() {
      this.boxes = [];
      this.activeBox = null;
      this.draft = null;
      this.drag = null;
      this.emitBoxes();
    },
    boxStyle(box) {
      const x0 = Math.min(box.x0, box.x1);
      const y0 = Math.min(box.y0, box.y1);
      const x1 = Math.max(box.x0, box.x1);
      const y1 = Math.max(box.y0, box.y1);
      return {
        left: `${x0 * 100}%`,
        top: `${y0 * 100}%`,
        width: `${(x1 - x0) * 100}%`,
        height: `${(y1 - y0) * 100}%`,
      };
    },
    pointFromEvent(event) {
      const layer = this.$refs.drawLayer;
      const rect = layer?.getBoundingClientRect();
      if (!rect || !rect.width || !rect.height) return { x: 0, y: 0 };
      return {
        x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
        y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
      };
    },
    captureDraw(event) {
      const layer = this.$refs.drawLayer;
      if (layer && event.pointerId != null) layer.setPointerCapture(event.pointerId);
    },
    onDrawDown(event) {
      if (!this.manual || event.button !== 0) return;
      const point = this.pointFromEvent(event);
      this.drag = { mode: "draw", x: point.x, y: point.y };
      this.draft = { x0: point.x, y0: point.y, x1: point.x, y1: point.y };
      this.captureDraw(event);
    },
    startMove(index, event) {
      if (event.button !== 0) return;
      const point = this.pointFromEvent(event);
      const box = this.boxes[index];
      this.activeBox = index;
      this.drag = { mode: "move", index, x: point.x, y: point.y, ox: box.x0, oy: box.y0 };
      this.captureDraw(event);
    },
    startResize(index, event) {
      if (event.button !== 0) return;
      this.activeBox = index;
      this.drag = { mode: "resize", index };
      this.captureDraw(event);
    },
    onDrawMove(event) {
      if (!this.drag) return;
      const point = this.pointFromEvent(event);
      if (this.drag.mode === "draw") {
        this.draft = { x0: this.drag.x, y0: this.drag.y, x1: point.x, y1: point.y };
        return;
      }
      if (this.drag.mode === "move") {
        const box = this.boxes[this.drag.index];
        const width = box.x1 - box.x0;
        const height = box.y1 - box.y0;
        let x0 = this.drag.ox + (point.x - this.drag.x);
        let y0 = this.drag.oy + (point.y - this.drag.y);
        x0 = Math.min(Math.max(0, x0), 1 - width);
        y0 = Math.min(Math.max(0, y0), 1 - height);
        this.boxes.splice(this.drag.index, 1, { x0, y0, x1: x0 + width, y1: y0 + height });
        return;
      }
      if (this.drag.mode === "resize") {
        const box = { ...this.boxes[this.drag.index] };
        box.x1 = Math.min(1, Math.max(box.x0 + 0.02, point.x));
        box.y1 = Math.min(1, Math.max(box.y0 + 0.02, point.y));
        this.boxes.splice(this.drag.index, 1, box);
      }
    },
    onDrawUp() {
      if (!this.drag) return;
      if (this.drag.mode === "draw" && this.draft) {
        const width = Math.abs(this.draft.x1 - this.draft.x0);
        const height = Math.abs(this.draft.y1 - this.draft.y0);
        if (width > 0.01 && height > 0.01) {
          this.boxes.push({
            x0: Math.min(this.draft.x0, this.draft.x1),
            y0: Math.min(this.draft.y0, this.draft.y1),
            x1: Math.max(this.draft.x0, this.draft.x1),
            y1: Math.max(this.draft.y0, this.draft.y1),
          });
          this.activeBox = this.boxes.length - 1;
        }
      }
      this.drag = null;
      this.draft = null;
      this.emitBoxes();
    },
    queueReload() {
      if (this.reloadQueued) return;
      this.reloadQueued = true;
      this.$nextTick(() => {
        this.reloadQueued = false;
        this.reload();
      });
    },
    async readError(error) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          if (typeof json.detail === "string" && json.detail) return json.detail;
          if (Array.isArray(json.detail)) {
            return json.detail.map((d) => d.msg || String(d)).join("; ");
          }
        } catch {
          /* response body was not JSON */
        }
      }
      return formatOcrApiError(error, "Page preview unavailable.");
    },
    revoke() {
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = "";
      }
    },
    changePage(delta) {
      const next = Math.min(Math.max(0, this.pageIndex + delta), this.maxPage);
      this.$emit("update:pageIndex", next);
    },
    async reload() {
      const requestId = this.reloadToken + 1;
      this.reloadToken = requestId;
      this.revoke();
      this.error = "";
      if (!this.filename) {
        this.loading = false;
        return;
      }
      this.loading = true;
      const path = this.tableIndex != null
        ? `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/preview`
        : `api/v1/reports/${encodeURIComponent(this.filename)}/pages/${this.pageIndex}/preview`;
      const params = this.tableIndex != null
        ? (this.imageRevision ? { rev: this.imageRevision } : undefined)
        : {
            highlight: this.highlightTables ? "true" : "false",
            ...(this.activeTableIndex != null ? { table_index: this.activeTableIndex } : {}),
            ...(this.imageRevision ? { rev: this.imageRevision } : {}),
          };
      let lastError = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        if (requestId !== this.reloadToken) return;
        try {
          const { data } = await ocrApi.get(path, { responseType: "blob", params });
          if (requestId !== this.reloadToken) return;
          this.objectUrl = URL.createObjectURL(data);
          lastError = null;
          break;
        } catch (e) {
          lastError = e;
          if (attempt === 0 && requestId === this.reloadToken) {
            await new Promise((resolve) => setTimeout(resolve, 400));
          }
        }
      }
      if (requestId !== this.reloadToken) return;
      if (lastError) this.error = await this.readError(lastError);
      this.loading = false;
    },
  },
};
</script>
